import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { getOneMonthDate } from 'components/shared/processes'
import {
	aggregateDotsByMonthAverage,
	isDateRangeLongerThanMonths,
} from 'components/shared/processes/chartDateAggregation'
import { IFatnessGraphResponse } from 'models/ICattlesData'
import { useMemo, useState } from 'react'
import CattlesApiService from 'services/cattlesApiServices'

interface IFatnessChartInfo {
	groupId: string | number
}

const useFatnessInfoForGraph = (props: IFatnessChartInfo) => {
	const { groupId } = props
	const monthDates = getOneMonthDate()
	const [dateRange, setDateRange] = useState({
		startDate: monthDates.startDate,
		endDate: monthDates.endDate,
	})
	const {
		data: filteredFatnessArray = [],
		isLoading,
		error,
	} = useQuery<IFatnessGraphResponse['lines'], AxiosError>({
		queryKey: ['fatnessGraph', dateRange, groupId],
		queryFn: async () => {
			return CattlesApiService.getFatnessGraph(
				dateRange.startDate,
				dateRange.endDate,
				groupId === 'all' ? null : groupId,
			).then(response => response.lines)
		},
		retry: false,
		staleTime: 1000 * 60 * 5, // 5 минут
	})
	const onChangeCallback = value => {
		setDateRange({
			startDate: value[0],
			endDate: value[1],
		})
	}
	const shouldAggregateMonthly = useMemo(
		() => isDateRangeLongerThanMonths(dateRange, 3),
		[dateRange],
	)
	const chartLines = useMemo(
		() =>
			filteredFatnessArray.map(line => ({
				...line,
				dots: shouldAggregateMonthly
					? aggregateDotsByMonthAverage(line.dots)
					: line.dots,
			})),
		[filteredFatnessArray, shouldAggregateMonthly],
	)
	const {
		normalCategories,
		deviationCategories,
		obesityCategories,
		emaciationCategories,
		timestamp,
	} = chartLines.reduce(
		(acc, category) => {
			const categoryData = category.dots.map(el => el.value)
			const normalizedLabel = category.label.trim().toLowerCase()

			if (acc.timestamp.length === 0) {
				acc.timestamp.push(...category.dots.map(el => el.date_ms ?? el.date))
			}

			if (normalizedLabel === 'нормальная' || normalizedLabel === 'в норме') {
				acc.normalCategories.push(...categoryData)
			} else if (normalizedLabel === 'с отклонением') {
				acc.deviationCategories.push(...categoryData)
			} else if (normalizedLabel === 'с ожирением') {
				acc.obesityCategories.push(...categoryData)
			} else if (normalizedLabel === 'с истощением') {
				acc.emaciationCategories.push(...categoryData)
			}

			return acc
		},
		{
			normalCategories: [] as number[],
			deviationCategories: [] as number[],
			obesityCategories: [] as number[],
			emaciationCategories: [] as number[],
			timestamp: [] as Array<number | string>,
		},
	)

	return {
		normalCategories,
		deviationCategories,
		obesityCategories,
		emaciationCategories,
		timestamp,
		onChangeCallback,
		dateRange,
		error,
		isLoading,
		shouldAggregateMonthly,
	}
}

export default useFatnessInfoForGraph
